import { useCallback, useEffect, useRef } from 'react'
import type { FlatList } from 'react-native'
import type { LogRow } from '../lib/logFeed'

/** Variable-height rows need a measured pass before scrollToIndex can center. */
export function useCenteredLogSelection(selectedIndex: number) {
  const listRef = useRef<FlatList<LogRow>>(null)
  const retry = useRef<ReturnType<typeof setTimeout> | null>(null)
  const attempts = useRef(0)
  const pending = useRef(false)
  const center = useCallback(() => {
    if (selectedIndex < 0 || !pending.current) return
    pending.current = false
    listRef.current?.scrollToIndex({ index: selectedIndex, viewPosition: 0.5, animated: false })
  }, [selectedIndex])
  useEffect(() => {
    attempts.current = 0
    pending.current = selectedIndex >= 0
    // Let FlatList commit the data and viewport before requesting the index.
    retry.current = setTimeout(center, 0)
    return () => { if (retry.current) clearTimeout(retry.current) }
  }, [selectedIndex, center])
  const onScrollToIndexFailed = useCallback((info: { index: number; averageItemLength: number }) => {
    if (info.index !== selectedIndex || attempts.current >= 12) return
    attempts.current += 1
    // Move the virtualization window to the target, then center using its
    // actual height once that batch has mounted. This is expected, not an error.
    listRef.current?.scrollToOffset({ offset: Math.max(0, info.averageItemLength * info.index), animated: false })
    pending.current = true
    if (retry.current) clearTimeout(retry.current)
    retry.current = setTimeout(center, 100)
  }, [selectedIndex, center])
  const onLayout = useCallback(() => {
    // Re-center after the map/log viewport changes (rotation or sheet exit).
    pending.current = selectedIndex >= 0
    attempts.current = 0
    center()
  }, [selectedIndex, center])
  return { listRef, onScrollToIndexFailed, onLayout, onContentSizeChange: center }
}
